import React, { useEffect } from 'react';
import { FixedWithContainer } from '../common/CardBoardStyled';
import { DropdownPayments } from './components/DropdownPayments';
import { TotalDebt } from './components/TotalDebt';
import { FullCycleDiscount } from './components/FullCycleDiscount';
import { DivisionLine } from '../courses/modules/myCourses/components/MyCoursesCollapsibles/styled';
import { PayItemsByPeriod } from './components/PayItemsByPeriod';
import { EmptyPayments, PaymentsSection } from './components/styled';
import EmptyIcon from '../../../assets/icons/events/noevents.svg';
import Spinner from '@/presentation/components/Spinner';
import SectionHeader from '@/presentation/reusables/SectionHeader';
import ReusableButton from '@/presentation/reusables/Button';
import PaymentContainer from './components/ContainerPayments';
import ErrorPayments from '@/presentation/components/ErrorPyaments';
import { PaymentStatus } from './constants';
import EmptyDataStateMessage from '@/presentation/reusables/EmptyDataStateMessage';

interface PaymentViewProps {
  handlerRedirect: (route: string) => void;
  pendingPayments: any;
  pendingSummary: any;
  paidPayments: any;
  dataError: any;
  hasProntoPago: boolean;
  dataBenefit?: any;
  periodsPending: string[];
  periodsPaid: string[];
  isLoadingPending: boolean;
  isSuccessPending: boolean;
  isLoadingPaid: boolean;
  loading: boolean;
}

const PaymentsView = ({
  handlerRedirect,
  pendingPayments,
  pendingSummary,
  dataError,
  paidPayments,
  hasProntoPago,
  dataBenefit,
  periodsPending,
  periodsPaid,
  isLoadingPending,
  isSuccessPending,
  isLoadingPaid,
  loading
}: PaymentViewProps) => {
  useEffect(() => {
    document.title = `UTP + Portal | Pagos`;
    return () => {
      document.title = `UTP+ Portal | Universidad Tecnológica del Perú`;
    };
  }, []);

  const hasExpiredPaymentProntoPago = (period: string) =>
    pendingPayments
      ?.filter((item: any) => item.periodDesc === period)
      ?.every(
        (item: any) =>
          Boolean(item.status === PaymentStatus.expired) ||
          Boolean(!item.hasDiscountProntoPago)
      ) ?? false;

  return (
    <PaymentContainer>
      <FixedWithContainer>
        <div className="payments">
          <SectionHeader
            title="Pagos"
            description="Revisa tus pagos pendientes, historial de pagos realizados y
            descubre tus opciones de pago."
          />
          {loading && <Spinner data-testid="spinner" />}
          {!loading &&
            (!dataError ? (
              <div>
                {dataBenefit?.requestBenefit && (
                  <FullCycleDiscount
                    dataCycleDiscount={dataBenefit}
                    itemFullCycle={pendingPayments?.find(
                      (i: any) => i.isFullCycleDiscount
                    )}
                    handlerRedirect={handlerRedirect}
                  />
                )}

                <ReusableButton
                  outlined
                  onClick={() => handlerRedirect('/pagos/opciones-pago')}
                  style={{ marginBottom: '24px' }}
                >
                  Opciones de pago
                </ReusableButton>

                <PaymentsSection>
                  <DropdownPayments
                    title={'Pendientes'}
                    itStartsOpened
                    itemsNumber={pendingPayments?.length ?? 0}
                    isLoading={isLoadingPending}
                    childContent={
                      <div>
                        <DivisionLine backgroundColor="var(--button-icon-focus)" />
                        {isLoadingPending && !isSuccessPending ? (
                          <Spinner data-testid="spinner" />
                        ) : (
                          <>
                            <h3>{dataError}</h3>
                            {periodsPending.length > 0 ? (
                              periodsPending?.map((period: string) => (
                                <PayItemsByPeriod
                                  key={period}
                                  hasExpiredPayment={hasExpiredPaymentProntoPago(
                                    period
                                  )}
                                  hasProntoPago={hasProntoPago}
                                  isDiscountFullCycleActive={pendingPayments?.some(
                                    (i: any) => i.isFullCycleDiscount
                                  )}
                                  paymentsArray={pendingPayments}
                                  period={period}
                                  currentPay={0}
                                />
                              ))
                            ) : (
                              <EmptyPayments>
                                <img src={EmptyIcon} alt="" />
                                Sin pagos pendientes. ¡Estás al día!{' '}
                              </EmptyPayments>
                            )}
                            <TotalDebt
                              totalPendingAmount={
                                pendingSummary?.totalPendingAmount
                              }
                              description="Deuda total"
                            />
                          </>
                        )}
                      </div>
                    }
                  />
                  <DropdownPayments
                    title={'Realizados'}
                    itStartsOpened
                    itemsNumber={paidPayments?.length ?? 0}
                    isLoading={isLoadingPaid}
                    childContent={
                      <div>
                        {paidPayments?.length > 0 ? (
                          <>
                            <DivisionLine backgroundColor="var(--button-icon-focus)" />
                            {periodsPaid?.map((period: string) => (
                              <PayItemsByPeriod
                                key={period}
                                paymentsArray={paidPayments}
                                period={period}
                                currentPay={0}
                              />
                            ))}
                          </>
                        ) : (
                          <EmptyDataStateMessage
                            message="Aún no tienes pagos realizados"
                            layout="row"
                          />
                        )}
                      </div>
                    }
                  />
                </PaymentsSection>
                <ReusableButton
                  outlined
                  text="Ver historial de pagos"
                  onClick={() => handlerRedirect('/pagos/historial')}
                />
              </div>
            ) : (
              <ErrorPayments />
            ))}
        </div>
      </FixedWithContainer>
    </PaymentContainer>
  );
};

export default PaymentsView;
